/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DatePicker, Drawer, Select, Input, Modal, message } from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';
import { TripHistoryRCTable, ButtonWrapper, TripExportWarningModal } from 'components';
import { Box, Button, Flex, Text } from 'atoms';
import { ACL, FilterTitle, FilterTag } from 'components/common';
import {
	getTripListAction,
	exportDownloadSlice,
	getTripFilterList,
	getListAction,
} from 'store/pageSlice';
import { getTripListActionV2, getTripLocationHistoryV2, exportTripListV2 } from 'store/tripSliceV2';
import { getUIVersion } from 'utils/router-static-data';
import { TripHistoryTableConfig, DATE_FORMAT_WITH_TIME, initialPaginateConfig } from 'utils';
import { TRIP_STATUS_OPTIONS, TRIP_CLOSED_STATUS_VALUE } from 'utils/tripStatus';
import { useQueryParams, useCascadingTripFilters } from 'hooks';
import { FilterAtnButton } from 'utils/commonStyled';
import { ShowRouteMap } from './StoresRoutes';
import IntercityEmptyState, { EmptyStateText } from './IntercityEmptyState';
import {
	loadTripFilters,
	saveTripFilters,
	clearTripFilters,
	formatDateForAPI,
	formatEndDateForAPI,
	formatDateForRangePicker,
} from 'utils/datePersistence';

const { Search } = Input;

const LtSearchBar = styled(Search)`
	border: none;
`;

const allOption = {
	value: '',
	label: 'All',
	key: 'All',
};

const TripStatusOptions = [allOption, ...TRIP_STATUS_OPTIONS];

const tripTypeList = [
	{ value: 'regular', label: 'Regular' },
	{ value: 'adhoc', label: 'Adhoc' },
];
tripTypeList.unshift(allOption);

const validOptions = [
	{ value: 'true', label: 'Valid', key: true },
	{ value: 'false', label: 'Invalid', key: false },
];
validOptions.unshift(allOption);
function withClosedTripStatus(trip_status) {
	const isSelected = trip_status !== undefined && trip_status !== null && trip_status !== '';
	return isSelected ? trip_status : TRIP_CLOSED_STATUS_VALUE;
}

function populateOptions(data = [], showAll = true) {
	/**
	 * assign id & value to option list
	 */
	data = data?.map((element) => ({
		value: element?.id,
		label: element?.value,
	}));

	/**
	 * Add All option of array first
	 */
	if (showAll) {
		data.unshift(allOption);
	}

	return data;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function TripHistoryPage() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { RangePicker } = DatePicker;
	const [citiesList, setCitiesList] = useState('');
	const [clientsList, setClientsList] = useState('');
	const [projectsList, setProjectsList] = useState('');
	const [vendorsList, setVendorsList] = useState('');
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [open, setOpen] = useState(false);
	const [rowData, setRowData] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectTripLocations, setTripLocations] = useState();
	const [paginateConfig, setpaginateConfig] = useQueryParams({
		search_type: 'trip',
		fetch_filters: false,
		business_unit_ids: [],
	});
	const [searchValue, setSearchValue] = useState('');
	useEffect(() => {
		setSearchValue(paginateConfig?.query_str || '');
	}, [paginateConfig?.query_str]);
	const [exportModalVisible, setExportModalVisible] = useState(false);
	const [exportWarningModal, setExportWarningModal] = useState(false);
	const [rangePickerValue, setRangePickerValue] = useState(null);
	// Force a one-time reload when arriving on this page to avoid stale state
	useEffect(() => {
		const reloadKey = `reload_once:${window.location.pathname}${window.location.search}`;
		if (!sessionStorage.getItem(reloadKey)) {
			sessionStorage.setItem(reloadKey, '1');
			window.location.replace(`${window.location.pathname}${window.location.search}`);
		}
	}, []);

	// Check UI version to determine which API to use
	const isV2UI = getUIVersion() === 'new';
	const { options: cascadeOptions } = useCascadingTripFilters({
		paginateConfig,
		enabled: isV2UI && open,
		isEnded: true,
	});
	useEffect(() => {
		if (!isV2UI) return;
		setCitiesList([allOption, ...(cascadeOptions.cities || [])]);
		setClientsList([allOption, ...(cascadeOptions.clients || [])]);
		setProjectsList([allOption, ...(cascadeOptions.projects || [])]);
		setVendorsList([allOption, ...(cascadeOptions.vendors || [])]);
	}, [isV2UI, cascadeOptions]);

	// Helper function to handle business unit IDs (array vs string from URL)
	const getBusinessUnitIds = useCallback(() => {
		const buIds = paginateConfig?.business_unit_ids;
		let result = [];

		if (Array.isArray(buIds)) {
			result = buIds;
		} else if (buIds) {
			result = [buIds];
		}

		return result.map((businessUnitId) => String(businessUnitId).trim()).filter(Boolean);
	}, [paginateConfig?.business_unit_ids]);

	const memoizedDispatchAction = useCallback(
		(params) => {
			if (isV2UI) {
				return getTripListActionV2({
					...params,
					trip_category: 'history',
					trip_status: withClosedTripStatus(params?.trip_status),
					business_unit_ids: getBusinessUnitIds(),
				});
			}
			return getTripListAction({
				...params,
				fetch_filters: false,
				trip_category: 'history',
				business_unit_ids: getBusinessUnitIds(),
			});
		},
		[isV2UI, getBusinessUnitIds]
	);

	// Helper: Set filter list based on filter type
	const setFilterList = useCallback((filter, options) => {
		switch (filter) {
			case 'client':
				setClientsList(options);
				break;
			case 'project':
				setProjectsList(options);
				break;
			case 'vendor':
				setVendorsList(options);
				break;
			default:
				break;
		}
	}, []);

	// Helper: Load city filter (always V1)
	const loadCityFilter = useCallback(() => {
		dispatch(getTripFilterList('city'))
			.then((res) => {
				const { rows = [] } = res;
				const cities = rows?.cities?.map((element) => ({
					value: element,
					label: element,
				}));
				cities.unshift(allOption);
				setCitiesList(cities);
			})
			.catch((error) => {
				console.error('Error fetching city filter:', error);
				setCitiesList([allOption]);
			});
	}, [dispatch]);

	// Helper: Load V1 filters (client, project, vendor)
	const loadV1Filter = useCallback(
		(filter) => {
			dispatch(getTripFilterList(filter))
				.then((res) => {
					const { rows = [] } = res;
					let options = [];

					if (filter === 'client') {
						options = populateOptions(rows.clients);
					} else if (filter === 'project') {
						options = populateOptions(rows.projects);
					} else if (filter === 'vendor') {
						options = populateOptions(rows.vendors);
					}

					setFilterList(filter, options);
				})
				.catch((error) => {
					console.error(`Error fetching ${filter} filter (V1):`, error);
					setFilterList(filter, [allOption]);
				});
		},
		[dispatch, setFilterList]
	);

	// Helper: Load business units (always V1)
	const loadBusinessUnits = useCallback(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
				version: 'v1',
			})
		)
			.then((res) => {
				const buOptions = (res?.rows || []).map((bu) => ({
					value: String(bu.id),
					label: bu.name,
				}));
				setBusinessUnitsList(buOptions);
			})
			.catch((error) => {
				console.error('Error fetching business unit filter:', error);
				setBusinessUnitsList([]);
			});
	}, [dispatch]);

	// Function to load filter data when filter drawer opens
	const loadFilterData = useCallback(() => {
		if (isV2UI) {
			loadBusinessUnits();
			return;
		}

		loadCityFilter();
		['client', 'project', 'vendor'].forEach((filter) => loadV1Filter(filter));
		loadBusinessUnits();
	}, [isV2UI, loadCityFilter, loadV1Filter, loadBusinessUnits]);

	// Calculate business unit filter value when business units list or IDs change
	useEffect(() => {
		const buIds = getBusinessUnitIds();
		if (buIds.length === 0) {
			setBusinessUnitFilterValue('');
			return;
		}

		// Ensure businessUnitsList is an array before using find
		if (!Array.isArray(businessUnitsList) || businessUnitsList.length === 0) {
			setBusinessUnitFilterValue('Business Unit');
			return;
		}

		// Get the business unit names for display
		const buNames = buIds
			.map((id) => {
				const businessUnit = businessUnitsList.find((item) => item.value === id);
				if (!businessUnit) {
					console.log(
						'Business unit not found for ID:',
						id,
						'Available options:',
						businessUnitsList
					);
				}
				return businessUnit ? businessUnit.label : id;
			})
			.filter(Boolean);

		setBusinessUnitFilterValue(buNames.length > 0 ? 'Business Unit' : '');
	}, [businessUnitsList, paginateConfig?.business_unit_ids, getBusinessUnitIds]);

	// Load persisted date range and filters on component mount
	useEffect(() => {
		const { dateRange, filters } = loadTripFilters('TRIP_HISTORY');

		// Load date range
		if (dateRange?.from_date_moment && dateRange?.to_date_moment) {
			const fromDate = formatDateForRangePicker(dateRange.from_date_moment);
			const toDate = formatDateForRangePicker(dateRange.to_date_moment);
			if (fromDate && toDate) {
				setRangePickerValue([fromDate, toDate]);

				// Also set the API parameters for the date range
				const apiFromDate = formatDateForAPI(fromDate);
				const apiToDate = formatEndDateForAPI(toDate);
				setpaginateConfig((prev) => ({
					...prev,
					from_date: apiFromDate,
					to_date: apiToDate,
				}));
			}
		}

		// Load other filters
		if (filters) {
			setpaginateConfig((prev) => ({
				...prev,
				...filters,
			}));
		}
	}, [setpaginateConfig]);

	// Sync RangePicker UI with URL/state-provided UTC dates
	useEffect(() => {
		if (paginateConfig?.from_date && paginateConfig?.to_date) {
			const fromUtc = moment.utc(paginateConfig.from_date, 'MM/DD/YYYY HH:mm:ss');
			const toUtc = moment.utc(paginateConfig.to_date, 'MM/DD/YYYY HH:mm:ss');
			if (fromUtc.isValid() && toUtc.isValid()) {
				const fromLocal = fromUtc.add(330, 'minutes');
				const toLocal = toUtc.add(330, 'minutes');
				setRangePickerValue([fromLocal, toLocal]);
			}
		}
	}, [paginateConfig?.from_date, paginateConfig?.to_date]);

	// Save filters to localStorage whenever paginateConfig changes (excluding date changes)
	useEffect(() => {
		// Don't save on initial load
		if (Object.keys(paginateConfig).length > 0) {
			// Extract filters excluding date fields
			const { from_date, to_date, ...filters } = paginateConfig;

			// Only save if there are actual filters
			if (Object.keys(filters).length > 0) {
				saveTripFilters('TRIP_HISTORY', null, filters);
			}
		}
	}, [paginateConfig]);

	const StatusValue = paginateConfig?.is_active ? 'Status' : '';
	const UserVal = paginateConfig?.user_type ? 'User Type' : '';
	const cityFilterValue = paginateConfig?.city ? 'City' : '';

	const [businessUnitFilterValue, setBusinessUnitFilterValue] = useState('');

	const client = paginateConfig?.client_id ? 'Client' : '';
	const project = paginateConfig?.project_id ? 'Project' : '';
	const StatusValueAll = paginateConfig?.is_active === '' ? 'Status' : '';
	const UserAllVal = paginateConfig?.user_type === '' ? 'User Type' : '';
	const clientAll = paginateConfig?.client_id === '' ? 'Client' : '';
	const projectAll = paginateConfig?.project_id === '' ? 'Project' : '';
	const cityAllValue = paginateConfig?.city === '' ? 'City' : '';
	const vendorValue = paginateConfig?.vendor_id === '' ? 'Vendor' : '';
	const VendorAllValue = paginateConfig?.vendor_id ? 'Vendor' : '';
	const tripStatus = paginateConfig?.trip_status ? 'Status' : '';
	const tripAllStatus = paginateConfig?.trip_status === '' ? 'Status' : '';
	const tripType = paginateConfig?.trip_type ? 'Type' : '';
	const tripAllType = paginateConfig?.trip_type === '' ? 'Type' : '';
	const All_is_valid = paginateConfig?.is_valid === '' ? 'Valid' : '';
	const All_is_valid_fasle = paginateConfig?.is_valid === false ? 'Valid' : '';
	const is_valid = paginateConfig?.is_valid ? 'Valid' : '';

	const createExport = (toDate, fromDate) => {
		if (isV2UI) {
			// V2 export: pass the same params the History search uses
			const params = {
				...paginateConfig,
				trip_category: 'history',
				trip_status: withClosedTripStatus(paginateConfig?.trip_status),
				fetch_filters: false,
				business_unit_ids: getBusinessUnitIds(),
				from_date: fromDate,
				to_date: toDate,
			};
			dispatch(exportTripListV2(params)).then((res) => {
				if (res?.rows?.download_url) {
					const count = res.rows.total_records;
					message.success(
						typeof count === 'number'
							? `Export ready — ${count} record${
									count === 1 ? '' : 's'
							  } (download started).`
							: 'Export download started.'
					);
					return;
				}
				if (res?.rows) {
					setExportModalVisible(true);
				}
			});
		} else {
			// V1 export format
			const payload = {
				create_type: 'download_task',
				data: {
					type: 'trip_export',
					filter_data: {
						to_date: toDate,
						from_date: fromDate,
						city: paginateConfig?.city,
						project_id: paginateConfig?.project_id,
						vendor_id: paginateConfig?.vendor_id,
						client_id: paginateConfig?.client_id,
						trip_type: paginateConfig?.trip_type,
						trip_status: paginateConfig?.trip_status,
						is_valid: getFilterData(paginateConfig?.is_valid),
						query_str: paginateConfig?.query_str,
					},
				},
			};
			dispatch(exportDownloadSlice({ data: payload, openNewWindow: false })).then((res) => {
				if (res?.rows) {
					setExportModalVisible(true);
				}
			});
		}
	};

	const handleExportClick = () => {
		const toDate = paginateConfig?.to_date;
		const fromDate = paginateConfig?.from_date;

		if (!toDate || !fromDate || toDate === undefined || fromDate === undefined) {
			setExportWarningModal(true);
		} else {
			const diff = moment(toDate).diff(moment(fromDate), 'days');
			if (diff > 365) {
				setExportWarningModal(true);
			} else {
				createExport(toDate, fromDate);
			}
		}
	};

	const handleExportWarningModalConfirm = () => {
		const toDate = moment(new Date()).format(DATE_FORMAT_WITH_TIME);
		const fromDate = moment(toDate).subtract(60, 'days').format(DATE_FORMAT_WITH_TIME);

		setExportWarningModal(false);
		createExport(toDate, fromDate);
	};

	const handleExportWarningModalCancel = () => {
		setExportWarningModal(false);
	};

	const regx = /true/;

	// eslint-disable-next-line consistent-return
	function getFilterData(valid) {
		if (valid === '') {
			return 'All';
		}
		if (valid === undefined) {
			return 'not_selected';
		}
		if (regx.test(valid)) {
			return true;
		}
		if (regx.test(valid) === false && valid !== 'All' && valid !== undefined) {
			return false;
		}
	}

	function handleValidTripTypeChange(val) {
		const finalVal = val === '' && val !== undefined ? 'All' : val;
		setpaginateConfig((prev) => ({
			...prev,
			is_valid: finalVal,
			offset: 0,
			currentPage: 1,
		}));
	}

	// Individual filter clear functions
	const clearCityFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			city: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearClientFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			client_id: undefined,
			client_name: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearProjectFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			project_id: undefined,
			project_location_code: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearVendorFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			vendor_id: undefined,
			vendor_name: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearTripStatusFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			trip_status: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearValidFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			is_valid: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearBusinessUnitFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			business_unit_ids: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	function showTripLocationHistory(trip_id) {
		if (isV2UI) {
			dispatch(getTripLocationHistoryV2({ trip_id }))
				.then((res) => {
					setIsModalOpen(true);
					// V2 returns array directly, V1 returns object with rows
					setTripLocations(Array.isArray(res) ? res : res?.rows || res);
				})
				.catch((err) => {
					console.log('Error:', err);
				});
		} else {
			dispatch(getTripListAction({ search_type: 'trip_location_history', trip_id }))
				.then((res) => {
					setIsModalOpen(true);
					setTripLocations(res);
				})
				.catch((err) => {
					console.log('Error:', err);
				});
		}
	}

	const renderTableContent = () => {
		if (!isDateSelected && !hasSearch) {
			return (
				<IntercityEmptyState>
					<EmptyStateText>Please select a date range or use search</EmptyStateText>
				</IntercityEmptyState>
			);
		}

		if (!rowData) {
			return (
				<IntercityEmptyState>
					<EmptyStateText>No Trips Found</EmptyStateText>
				</IntercityEmptyState>
			);
		}

		return (
			<TripHistoryRCTable
				rowKey="id"
				tableDatas={[rowData, setRowData]}
				togglePaginate={[paginateConfig, setpaginateConfig]}
				dispatchAction={memoizedDispatchAction}
				columns={TripHistoryTableConfig(showTripLocationHistory, businessUnitsList)}
				scroll={{ x: true }}
			/>
		);
	};

	const isDateSelected = Boolean(paginateConfig?.from_date && paginateConfig?.to_date);
	const hasSearch = Boolean((paginateConfig?.query_str || '').trim());

	return (
		<>
			<Flex
				justifyContent="space-between"
				alignItems="center"
				mt={3}
				gap="16px"
				flexWrap="nowrap"
			>
				<Box flex="0 0 20%" marginRight="10px" minWidth="180px">
					<LtSearchBar
						placeholder="Search"
						enterButton="Search"
						allowClear
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						onSearch={(val) => {
							// Preserve selected date range in payload
							const preservedFrom =
								paginateConfig?.from_date ||
								(rangePickerValue?.[0]
									? formatDateForAPI(rangePickerValue[0])
									: undefined);
							const preservedTo =
								paginateConfig?.to_date ||
								(rangePickerValue?.[1]
									? formatEndDateForAPI(rangePickerValue[1])
									: undefined);

							setpaginateConfig({
								...initialPaginateConfig,
								query_str: val,
								search_type: 'trip',
								fetch_filters: false,
								offset: 0,
								currentPage: 1,
								...(preservedFrom ? { from_date: preservedFrom } : {}),
								...(preservedTo ? { to_date: preservedTo } : {}),
							});
						}}
					/>
				</Box>

				<Box
					flex="0 0 auto"
					display="flex"
					alignItems="center"
					flexWrap="nowrap"
					className="filter-highlight"
					gap="8px"
					marginRight="2"
				>
					<Box
						width="170px"
						height="37px"
						padding="10px 10px 10px 12px"
						background="#FFFFFF"
						border="1px solid #C6C6C8"
						borderRadius="2px"
						display="flex"
						alignItems="center"
						overflowX="auto"
						whiteSpace="nowrap"
						gap="8px"
						className="filter-tags-scroll-container"
					>
						{StatusValue && (
							<FilterTag
								onClose={() =>
									setpaginateConfig((prev) => ({
										...prev,
										is_active: undefined,
										offset: 0,
										currentPage: 1,
									}))
								}
							>
								{StatusValue}
							</FilterTag>
						)}
						{StatusValueAll && (
							<FilterTag
								onClose={() =>
									setpaginateConfig((prev) => ({
										...prev,
										is_active: undefined,
										offset: 0,
										currentPage: 1,
									}))
								}
							>
								{StatusValueAll}
							</FilterTag>
						)}
						{projectAll && (
							<FilterTag
								onClose={() =>
									setpaginateConfig((prev) => ({
										...prev,
										project_id: undefined,
										offset: 0,
										currentPage: 1,
									}))
								}
							>
								{projectAll}
							</FilterTag>
						)}
						{UserVal && (
							<FilterTag
								onClose={() =>
									setpaginateConfig((prev) => ({
										...prev,
										user_type: undefined,
										offset: 0,
										currentPage: 1,
									}))
								}
							>
								{UserVal}
							</FilterTag>
						)}
						{cityFilterValue && (
							<FilterTag onClose={clearCityFilter}>{cityFilterValue}</FilterTag>
						)}
						{businessUnitFilterValue && (
							<FilterTag onClose={clearBusinessUnitFilter}>
								{businessUnitFilterValue}
							</FilterTag>
						)}
						{client && <FilterTag onClose={clearClientFilter}>{client}</FilterTag>}
						{project && <FilterTag onClose={clearProjectFilter}>{project}</FilterTag>}
						{UserAllVal && (
							<FilterTag
								onClose={() =>
									setpaginateConfig((prev) => ({ ...prev, user_type: undefined }))
								}
							>
								{UserAllVal}
							</FilterTag>
						)}
						{clientAll && (
							<FilterTag onClose={clearClientFilter}>{clientAll}</FilterTag>
						)}
						{cityAllValue && (
							<FilterTag onClose={clearCityFilter}>{cityAllValue}</FilterTag>
						)}
						{VendorAllValue && (
							<FilterTag onClose={clearVendorFilter}>{VendorAllValue}</FilterTag>
						)}
						{vendorValue && (
							<FilterTag onClose={clearVendorFilter}>{vendorValue}</FilterTag>
						)}
						{tripStatus && (
							<FilterTag onClose={clearTripStatusFilter}>{tripStatus}</FilterTag>
						)}
						{tripAllStatus && (
							<FilterTag onClose={clearTripStatusFilter}>{tripAllStatus}</FilterTag>
						)}
						{tripType && (
							<FilterTag
								onClose={() =>
									setpaginateConfig((prev) => ({
										...prev,
										trip_type: undefined,
										offset: 0,
										currentPage: 1,
									}))
								}
							>
								{tripType}
							</FilterTag>
						)}
						{tripAllType && (
							<FilterTag
								onClose={() =>
									setpaginateConfig((prev) => ({
										...prev,
										trip_type: undefined,
										offset: 0,
										currentPage: 1,
									}))
								}
							>
								{tripAllType}
							</FilterTag>
						)}
						{All_is_valid && (
							<FilterTag onClose={clearValidFilter}>{All_is_valid}</FilterTag>
						)}
						{is_valid && <FilterTag onClose={clearValidFilter}>{is_valid}</FilterTag>}
						{All_is_valid_fasle && (
							<FilterTag onClose={clearValidFilter}>{All_is_valid_fasle}</FilterTag>
						)}
					</Box>
					<FilterAtnButton
						marginRight="1"
						onClick={() => {
							setOpen(true);
							loadFilterData();
						}}
					>
						FILTERS
					</FilterAtnButton>
				</Box>

				<Box flex="0 0 auto" display="flex" alignItems="center" gap="8px" minWidth="220px">
					<Text whiteSpace="nowrap" marginRight="1">
						Select Date:
					</Text>
					<RangePicker
						name="date_filter"
						style={{ minWidth: '200px', width: '100%' }}
						allowClear
						value={rangePickerValue}
						onChange={(val) => {
							setRangePickerValue(val);
							if (val) {
								const fromDate = formatDateForAPI(val[0]);
								const toDate = formatEndDateForAPI(val[1]);
								const newConfig = {
									...paginateConfig,
									from_date: fromDate,
									to_date: toDate,
									offset: 0,
									currentPage: 1,
								};
								setpaginateConfig(newConfig);

								// Save both dates and filters to localStorage
								saveTripFilters(
									'TRIP_HISTORY',
									{
										from_date: fromDate,
										to_date: toDate,
										from_date_moment: val[0],
										to_date_moment: val[1],
									},
									newConfig
								);
							} else {
								const newConfig = {
									...paginateConfig,
									from_date: false,
									to_date: false,
									offset: 0,
									currentPage: 1,
								};
								setpaginateConfig(newConfig);

								// Clear from localStorage
								clearTripFilters('TRIP_HISTORY');
							}
						}}
					/>
				</Box>

				<Box flex="0 0 auto" display="flex" gap="4px" minWidth="180px">
					<ACL action="bulk_upload" module="trip_bulk_upload">
						<ButtonWrapper
							onClick={() => navigate('/trips/bulk-upload')}
							icons={<PlusOutlined />}
						>
							BULK UPLOAD
						</ButtonWrapper>
					</ACL>
					<ButtonWrapper
						marginLeft="-1"
						onClick={handleExportClick}
						icons={<DownloadOutlined />}
					>
						EXPORT
					</ButtonWrapper>
				</Box>
			</Flex>
			<Box mt={4}>{renderTableContent()}</Box>
			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px">Business Unit</Text>
						<Select
							mode="multiple"
							showSearch
							style={{ width: '100%' }}
							name="business_unit_ids"
							value={getBusinessUnitIds()}
							onChange={(val) => {
								setpaginateConfig((prev) => ({
									...prev,
									business_unit_ids: val,
									offset: 0,
									currentPage: 1,
								}));
								if (val.length > 0) {
									message.warning(
										'Entities without an association to a Business Unit are not visible.'
									);
								}
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={businessUnitsList}
							maxTagCount="responsive"
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={paginateConfig?.city}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									city: val,
									project_id: undefined,
									project_location_code: undefined,
									offset: 0,
									currentPage: 1,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={citiesList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Client</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="client_name"
							value={paginateConfig?.client_id}
							onChange={(val) => {
								// Find the selected option to get the display label
								const selectedOption = clientsList?.find(
									(option) => option.value === val
								);
								setpaginateConfig((prev) => ({
									...prev,
									client_id: val,
									client_name: selectedOption?.label || undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={clientsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="project_location_code"
							value={paginateConfig?.project_id}
							onChange={(val) => {
								// Find the selected option to get the display label
								const selectedOption = projectsList?.find(
									(option) => option.value === val
								);
								setpaginateConfig((prev) => ({
									...prev,
									project_id: val,
									project_location_code: selectedOption?.label || undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={projectsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Vendor</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vendor_name"
							value={paginateConfig?.vendor_name}
							onChange={(val) => {
								// Find the selected option to get the display label
								const selectedOption = vendorsList?.find(
									(option) => option.value === val
								);
								setpaginateConfig((prev) => ({
									...prev,
									vendor_id: val,
									vendor_name: selectedOption?.label || undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={vendorsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Trip Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="trip_type"
							value={paginateConfig?.trip_type}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									trip_type: val,
									offset: 0,
									currentPage: 1,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={tripTypeList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Trip Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="trip_status"
							value={paginateConfig?.trip_status}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									trip_status: val,
									offset: 0,
									currentPage: 1,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={TripStatusOptions}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Valid</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="is_valid"
							value={paginateConfig?.is_valid}
							onChange={(val) => handleValidTripTypeChange(val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={validOptions}
						/>
					</Box>

					<Box>
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() => {
								clearTripFilters('TRIP_HISTORY');
								setRangePickerValue(null);
								setpaginateConfig({
									...initialPaginateConfig,
									search_type: 'trip',
									fetch_filters: false,
									business_unit_ids: [],
									offset: 0,
									currentPage: 1,
								});
								setOpen(false);
							}}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>
			<Modal
				title="Your Report Request is in progress"
				visible={exportModalVisible}
				onCancel={() => setExportModalVisible(false)}
				footer={null}
				style={{ top: 220, marginLeft: 'calc(50% - 200px)' }}
			>
				<p style={{ color: '#666666' }}>Report will be available in the Downloads page.</p>

				<Button
					type="primary"
					style={{ marginTop: '25px' }}
					onClick={() => {
						setExportModalVisible(false);
						navigate('/downloads');
					}}
				>
					Go to Downloads page
				</Button>
			</Modal>
			<TripExportWarningModal
				open={exportWarningModal}
				onConfirm={handleExportWarningModalConfirm}
				onCancel={handleExportWarningModalCancel}
			/>
			<ShowRouteMap
				isModalOpen={isModalOpen}
				tripdata={selectTripLocations}
				modalState={setIsModalOpen}
			/>
		</>
	);
}
